import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  HStack,
  IconButton,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import {
  CalendarContext,
  CalendarMonthStyles,
  MonthContext,
} from "@uselessdev/datepicker";
import {
  format as dateFormat,
  getMonth,
  getYear,
  setMonth,
  setYear,
} from "date-fns";
import { range } from "lodash-es";
import React, { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMonthNameList } from "shared/components/input/date/DateInputUtils";
import { Listbox, ListboxItem } from "shared/components/list/Listbox";

export const CalendarViewMonthYear: React.FC = () => {
  const { onSelectDates } = useContext(CalendarContext);

  const [activePicker, setActivePicker] = useState<"month" | "year">();
  const openMonthPicker = () => setActivePicker("month");
  const openYearPicker = () => setActivePicker("year");
  const closePicker = () => setActivePicker(undefined);

  const styles = useMultiStyleConfig("CalendarMonth") as CalendarMonthStyles;

  const onPickerSelectDate = (newDate: Date) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error FIXME: The correct behavior would be to set the "pending"
    //                   date (same as when pressing prev/next month buttons)
    //                   but the calendar library does not provide a way to do that.
    //                   For now the best option is to keep the calendar popup open.
    //                   This flag is checked in DateInput#onSelectDate.
    newDate.keepCalendarOpen = true;
    onSelectDates(newDate);
    // setTimeout is used here so the calendar can update to
    // display the new month/year before the overlay is closed.
    // This prevents flickering.
    setTimeout(() => {
      setActivePicker(undefined);
    });
  };

  const pickerProps = {
    onClose: closePicker,
    onSelectDate: onPickerSelectDate,
  };

  return (
    <>
      <HStack sx={styles.name} spacing="-4px">
        <HeadingButton format="LLLL" onClick={openMonthPicker} />
        <HeadingButton format="yyyy" onClick={openYearPicker} />
      </HStack>
      {activePicker === "month" && <MonthPicker {...pickerProps} />}
      {activePicker === "year" && <YearPicker {...pickerProps} />}
    </>
  );
};

const HeadingButton: React.FC<{
  format: string;
  onClick: () => void;
}> = ({ format, onClick }) => {
  const { dates, locale } = useContext(CalendarContext);
  const { month } = useContext(MonthContext);
  const currentMonth = dates[Number(month)].startDateOfMonth;
  return (
    <Button
      variant="subtle"
      textColor="black"
      onClick={onClick}
      py="2x"
      px="4px"
      minWidth={0}
      _hover={{
        bg: "primary.50",
      }}
    >
      {dateFormat(currentMonth, format, { locale })}
    </Button>
  );
};

const MonthPicker: React.FC<{
  onClose: () => void;
  onSelectDate: (date: Date) => void;
}> = ({ onClose, onSelectDate }) => {
  const { currentMonth, selectedMonth, selectedDate } = usePickerDates();
  return (
    <PickerOverlay onClose={onClose}>
      <Listbox>
        {getMonthNameList().map((monthName, monthIndex) => (
          <ListboxItem
            key={monthIndex}
            flexBasis="40%"
            isCurrent={monthIndex === currentMonth}
            isSelected={monthIndex === selectedMonth}
            onClick={() => {
              onSelectDate(setMonth(selectedDate, monthIndex));
            }}
          >
            {monthName}
          </ListboxItem>
        ))}
      </Listbox>
    </PickerOverlay>
  );
};

const YearPicker: React.FC<{
  onClose: () => void;
  onSelectDate: (date: Date) => void;
}> = ({ onClose, onSelectDate }) => {
  const { currentYear, selectedYear, selectedDate } = usePickerDates();
  return (
    <PickerOverlay onClose={onClose}>
      <Listbox>
        {range(1990, 2300).map((year) => (
          <ListboxItem
            key={year}
            flexBasis="40%"
            isCurrent={year === currentYear}
            isSelected={year === selectedYear}
            onClick={() => {
              onSelectDate(setYear(selectedDate, year));
            }}
          >
            {year}
          </ListboxItem>
        ))}
      </Listbox>
    </PickerOverlay>
  );
};

const PickerOverlay: React.FC<
  React.PropsWithChildren<{
    onClose: () => void;
  }>
> = ({ onClose, children }) => {
  const { t } = useTranslation(["common"]);
  return (
    <Box
      borderRadius={6}
      position="absolute"
      bg="white"
      top={0}
      left={0}
      right={0}
      bottom={0}
      zIndex={99}
    >
      <IconButton
        variant="ghost"
        aria-label={t("common:back")}
        icon={<ArrowBackIcon w="20px" h="20px" />}
        w="48px"
        h="48px"
        position="sticky"
        onClick={onClose}
        zIndex={99}
      />
      {children}
    </Box>
  );
};

const usePickerDates = () => {
  const { dates, startSelectedDate } = useContext(CalendarContext);
  const { month } = useContext(MonthContext);

  const date = dates[Number(month)];
  const selectedDate = startSelectedDate ?? date.startDateOfMonth;

  const currentDate = Date.now();
  const currentMonth = getMonth(currentDate);
  const currentYear = getYear(currentDate);

  // Refs are here to keep the values stable (even after selecting a new date)
  // to prevent flickering. This will all be re-created with the updated values
  // when the picker opens (mounts) again.
  const selectedMonth = useRef(getMonth(selectedDate)).current;
  const selectedYear = useRef(getYear(selectedDate)).current;

  return {
    currentYear,
    selectedYear,
    selectedDate,
    selectedMonth,
    currentMonth,
  };
};
